"use client";

import { useState } from "react";
import { Mail, Loader2, X, AlertCircle, Send, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  generateInvoiceChaseEmailsAction,
  fetchOverdueInvoices,
} from "@/lib/supabase/ai-actions";
import type { InvoiceChaseDraft } from "@/lib/gemini";

interface InvoiceChaseDialogProps {
  orgName: string;
  onSent?: () => void;
}

export function InvoiceChaseDialog({ orgName, onSent }: InvoiceChaseDialogProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [drafts, setDrafts] = useState<InvoiceChaseDraft[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [invoices, setInvoices] = useState<
    { id: string; invoice_number: string; client_name: string; total_cents: number; due_date: string }[]
  >([]);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  const handleOpen = async () => {
    setOpen(true);
    setError(null);
    setDrafts([]);
    setLoading(true);
    try {
      const result = await fetchOverdueInvoices();
      if (!result.success) {
        setError(result.error || "Failed to fetch overdue invoices");
      } else {
        setInvoices(result.invoices || []);
        setSelectedInvoices((result.invoices || []).map((i) => i.id));
      }
    } catch (e: any) {
      setError(e.message || "Failed to fetch overdue invoices");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (selectedInvoices.length === 0) {
      setError("Select at least one invoice to generate emails");
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      const result = await generateInvoiceChaseEmailsAction(selectedInvoices, orgName);
      if (!result.success) {
        if (result.blocked) {
          setError("You've reached your free tier limit for AI queries. Upgrade to Pro for unlimited access.");
        } else {
          setError(result.error || "Failed to generate emails");
        }
        return;
      }
      if (result.drafts) {
        setDrafts(result.drafts);
      }
    } catch (e: any) {
      setError(e.message || "Failed to generate emails");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = (body: string, idx: number) => {
    navigator.clipboard.writeText(body);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const handleSend = async (draft: InvoiceChaseDraft) => {
    // For now, just close — actual sending would integrate with email service
    alert("Email sent! (Integration with email service pending)");
    onSent?.();
  };

  const toggleInvoice = (id: string) => {
    setSelectedInvoices((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={handleOpen}
        className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-teal-600 transition-colors cursor-pointer"
      >
        <Mail className="w-4 h-4" />
        Generate Chase Emails with AI
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">AI Invoice Chase Emails</h2>
                  <p className="text-xs text-gray-500">Select overdue invoices to generate follow-up emails</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-gray-100 rounded-lg cursor-pointer">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: "calc(80vh - 140px)" }}>
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-3">
                  <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Invoice selection */}
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">No overdue invoices found</div>
              ) : (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Select Invoices ({selectedInvoices.length}/{invoices.length})
                    </span>
                    <button
                      onClick={() =>
                        setSelectedInvoices(
                          selectedInvoices.length === invoices.length ? [] : invoices.map((i) => i.id)
                        )
                      }
                      className="text-xs text-teal-600 hover:underline cursor-pointer"
                    >
                      {selectedInvoices.length === invoices.length ? "Deselect All" : "Select All"}
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {invoices.map((inv) => (
                      <label
                        key={inv.id}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors cursor-pointer ${
                          selectedInvoices.includes(inv.id)
                            ? "border-teal-300 bg-teal-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedInvoices.includes(inv.id)}
                          onChange={() => toggleInvoice(inv.id)}
                          className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">{inv.invoice_number}</p>
                          <p className="text-xs text-gray-500 truncate">{inv.client_name}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-semibold text-gray-900">
                            ${(inv.total_cents / 100).toFixed(2)}
                          </p>
                          <p className="text-[10px] text-red-500">Due: {inv.due_date}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Generate button */}
              {invoices.length > 0 && !loading && (
                <Button
                  onClick={handleGenerate}
                  disabled={generating || selectedInvoices.length === 0}
                  className="w-full bg-[#1D9E75] hover:bg-[#1a8b66] text-white gap-2 cursor-pointer disabled:opacity-50"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Generate AI Emails
                    </>
                  )}
                </Button>
              )}

              {/* Drafts */}
              {drafts.length > 0 && (
                <div className="mt-4 space-y-3">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Generated Drafts ({drafts.length})
                  </h3>
                  {drafts.map((draft, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
                        <div>
                          <p className="text-xs font-semibold text-gray-900">{draft.invoice_number}</p>
                          <p className="text-xs text-gray-500">{draft.subject}</p>
                        </div>
                        <button
                          onClick={() => handleCopy(draft.body, idx)}
                          className="p-1.5 hover:bg-white rounded-md text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                          title="Copy email body"
                        >
                          {copiedIdx === idx ? (
                            <Check className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      <div className="px-4 py-3">
                        <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                          {draft.body}
                        </pre>
                      </div>
                      <div className="bg-gray-50 px-4 py-2 border-t border-gray-100 flex justify-end">
                        <Button
                          size="sm"
                          onClick={() => handleSend(draft)}
                          className="bg-[#1D9E75] hover:bg-[#1a8b66] text-white gap-1.5 cursor-pointer"
                        >
                          <Send className="w-3.5 h-3.5" />
                          Send Email
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
